export const navItems = [
  { label: "Work", target: "work" },
  { label: "Approach", target: "approach" },
  { label: "Stack", target: "stack" },
  { label: "Path", target: "path" },
];

export const site = {
  name: "Moosa Ishfaq",
  short: "Moosa",
  role: "Full-Stack Engineer",
  location: "Lahore, PK · Remote",
};

export const hero = {
  // Headline is rendered as three stacked lines; `accent` is the payoff word.
  lines: ["I build", "full-stack products"],
  tail: "that ",
  accent: "ship.",
  desc: "Full-stack engineer building production systems for real businesses across Laravel, Next.js and the MERN stack. I wire in AI where it actually earns its place.",
  available: "Available for work",
  portrait: "assets/images/moosa.webp",
  cvHref: "assets/moosaCV.pdf",
};

/* Tech ticker under the hero — doubles as the credibility strip. */
export const ticker = [
  "React",
  "Next.js",
  "TypeScript",
  "Laravel",
  "PHP",
  "Node.js",
  "Express",
  "MongoDB",
  "MySQL",
  "Tailwind CSS",
  "Framer Motion",
  "OpenAI API",
  "MCP",
  "Python",
  "Flask",
  "REST APIs",
  "Stripe",
  "Git",
];

export const projects = {
  eyebrow: "Selected work",
  heading: "Work in production",
  desc: "Production apps and platforms, each one shipped and solving a problem someone actually had.",

  /* ────────────────────────────────────────────────────────────────
   * ARRAY ORDER = ORDER ON THE SITE. Newest first.
   * ADD A NEW PROJECT AT THE TOP OF THIS LIST — right below here.
   *
   * Do NOT add id/index fields. The card number (01, 02, …) and the
   * React key are derived from position in Projects.jsx, so inserting
   * at the top renumbers everything below it automatically.
   *
   * `category` accepts a string OR an array — use an array when a
   * project genuinely belongs in more than one bucket:
   *     category: ["Full-stack", "AI"],
   * Filters and their counts are derived from these values, so a new
   * category name creates its own filter chip with no other edits.
   * ──────────────────────────────────────────────────────────────── */
  list: [
    {
      category: "Full-stack",
      title: "Smart Campus Solution",
      subtitle:
        "School management suite — QR attendance, fee collection, exams, HRM & payroll, accounts & reporting for multi-campus schools",
      description:
        "The complete operating system for a school group: enrol students, plan and collect fees, take attendance by QR or by hand, run exams and publish results, manage staff and payroll, and keep the books — all from one portal, across every campus. Each school sees only its own students, fees and finances, with its own branding, languages and permissions.",
      features: [
        "QR attendance: students scan their ID card at the gate and the day's register fills itself, with a live scan log",
        "Manual attendance grids too — mark a class in one screen, then read daily, monthly, subject-wise and absent-reason reports",
        "Student & family records: admissions, enrollment, guardians, photos, documents, QR badges and printable ID cards",
        "Fee plans per student built from the class fee schedule, with month generation, arrears and instalments",
        "Fee collection: generate challans, take full or partial payments, apply discounts and late fines, print receipts",
        "Fee defaulters and money reports — month-wise, family-wise, class-wise, particular-wise and wallet-wise",
        "Examinations: create exams, enter marks, apply grading policies, publish result cards, date sheets and roll-no slips",
        "HRM & payroll: staff records, leave requests and approvals, shift timings, monthly salary generation and slips",
        "Accounts: income & expense, journal entries, general ledger, trial balance, profit & loss and balance sheet",
        "Dashboards for every module — admissions, fees, attendance, exams, HRM and accounts — all reading live data",
        "Printing centre: challans, ID cards, certificates, roll-no slips and per-school branded headers and footers",
        "SMS & WhatsApp: gateway setup, message templates and bulk notifications to guardians",
        "Parents app controls: notice board, exam results and login credentials for parent accounts",
        "Academics: timetables, syllabus, daily diary, teacher allocation, date sheets and test schedules",
        "Events calendar, complaints/feedback with replies, and a shared document library",
        "Multi-campus: switch campuses from one login, with separate classes, fees and branding per campus",
        "Roles & permissions: 121 permissions across 8 modules, with custom roles so staff see only their own screens",
        "Per-school branding & themes: logo, colours and app name drive a branded login and portal",
        "English and Urdu with a right-to-left layout, and a language switcher each school controls",
      ],
      technologies: [
        "PHP",
        "Laravel",
        "MySQL",
        "Blade",
        "Vite",
        "Tailwind CSS",
        "JavaScript",
        "jQuery",
        "DataTables",
        "Select2",
        "FullCalendar",
        "Chart.js",
        "Bootstrap",
        "QRCode.js",
        "TinyMCE",
      ],
      demoLink: "https://smartcampussolution.com/",
      imgSrc: "project_school.webp",
      featured: true,
    },
    {
      category: "Full-stack",
      title: "Rinavai",
      subtitle:
        "Multi-tenant company OS - Work, Chat, Docs, Voice, AI copilot & Messaging on a security-first architecture",
      description:
        "A multi-tenant company platform where Postgres row-level security, typed authorization and a hash-chained audit log make entire classes of security bugs impossible to ship rather than merely discouraged. Work, chat, docs, calls, an AI copilot and GitHub PR review all share one tenancy and policy layer.",
      features: [
        "Kanban/list project boards with drag-and-drop ranking, sprints, calendar view, custom fields & saved filters",
        "Real-time chat with channels, DMs, threads, reactions & compliance retention",
        "Collaborative docs on Yjs/Hocuspocus with live cursors, comments & version restore",
        "In-app WebRTC voice calls with TURN credential gating & consent-based recording",
        "Outbound calling & SMS via Twilio with spend caps, geo-fencing & carrier webhooks",
        "AI copilot with tool-calling across Work, Chat, Docs and GitHub PRs, with confirm-before-execute and per-org spend budgets",
        "GitHub PR integration: review tools, card↔PR/branch links, one-click branch creation and inline diff views",
        "Standup view with Yesterday/Today/Overdue/Urgent buckets, optional AI narration & daily email digests",
        "Automation engine with event triggers, actions & outbound webhooks, plus velocity/burndown/cycle-time analytics",
        "Guest access via project-scoped tuples, org-level permission grants & email invitations",
        "Row-level-security multi-tenancy: a missing org filter returns zero rows, never another tenant's data",
        "Passkey (WebAuthn) + TOTP MFA with reuse-detected refresh token rotation",
        "Hash-chained, tamper-evident audit log for every state-mutating action",
        "TQL query language powering saved search & filters across Work, Chat and Docs",
        "Stripe billing with plan catalog & entitlements behind a platform admin console (org suspension, flag overrides, operator audit)",
        "Per-card calendar sync via opt-in live ICS feeds",
        "Virus-scanned, MIME-pinned file attachments via presigned S3 uploads",
        "Expo/React Native mobile app sharing the same typed tRPC contracts",
      ],
      technologies: [
        "TypeScript",
        "React",
        "Vite",
        "tRPC",
        "Node.js",
        "Fastify",
        "PostgreSQL",
        "Drizzle ORM",
        "Socket.io",
        "Hocuspocus",
        "Yjs",
        "WebRTC",
        "Twilio",
        "Stripe",
        "MinIO",
        "Zod",
        "Expo",
        "React Native",
      ],
      demoLink: "https://rinavai.vercel.app/",
      imgSrc: "project_rinavai.webp",
      featured: true,
    },
    {
      category: "Full-stack",
      title: "QuickPilot",
      subtitle:
        "AI productivity assistant on Telegram with billing, automations & multi-provider AI",
      description:
        "A full-stack AI productivity assistant delivered through Telegram, powered by a channel-agnostic AI layer on a Laravel backend. Multi-provider AI routing, Stripe subscriptions, referral growth loops, admin broadcast automations and a live analytics dashboard.",
      features: [
        "Unified chat pipeline for text, voice, images & documents",
        "Multi-provider AI routing (OpenAI, Claude, Gemini, AgentRouter) behind one interface",
        "Voice transcription (Whisper) and PDF/URL summarization",
        "Natural-language reminders delivered via delayed queue jobs",
        "Group chat support with mention-scoped, privacy-aware replies",
        "Stripe-powered subscriptions, free trials & plan management (Cashier)",
        "Referral program with automatic bonus attribution",
        "Usage-based upgrade nudges on rolling token limits",
        "Templated admin broadcasts with trigger-based automations",
        "Filament admin dashboard for users, plans, revenue & AI cost analytics",
        "Fully queued, webhook-driven architecture (Horizon + Redis)",
      ],
      technologies: [
        "PHP",
        "Laravel",
        "PostgreSQL",
        "Redis",
        "Horizon",
        "Telegram Bot SDK",
        "Stripe",
        "Cashier",
        "Filament",
        "Pest",
      ],
      demoLink: "https://t.me/quickpilot_ai_bot",
      imgSrc: "project_quickpilot.webp",
      featured: true,
    },
    {
      category: "Full-stack",
      title: "GoFly",
      subtitle:
        "Travel & visa agency platform with booking, marketplace & AI analytics",
      description:
        "A full-stack travel and visa agency platform: tour packages, hotel bookings, experiences and visa services in a React SPA on a decoupled Laravel REST API. Stripe checkout, a KYC-gated multi-vendor marketplace, admin panel and MCP analytics.",
      features: [
        "Destination, package, hotel & experience discovery with booking",
        "Visa services with requirements and consultation scheduling",
        "Travel merchandise storefront alongside bookings",
        "Stripe-powered checkout, cart, and order management",
        "Multi-vendor marketplace with identity (KYC) & vendor verification",
        "Verified-booking reviews across packages, hotels & guides",
        "SEO-friendly blog and travel-inspiration content",
        "Admin panel for catalog, bookings, orders & content",
        "Token-secured REST API with a decoupled React SPA",
        "Read-only MCP server for natural-language business analytics",
        "Fully responsive, accessible interface",
      ],
      technologies: [
        "PHP",
        "Laravel",
        "Sanctum",
        "MySQL",
        "Stripe",
        "React",
        "TypeScript",
        "Vite",
        "Bootstrap",
      ],
      demoLink: "https://gofly-fyp.vercel.app/",
      imgSrc: "project_gofly.webp",
      featured: true,
    },
    {
      category: "AI",
      title: "Hikmat AI",
      subtitle: "AI lesson-plan generator for Urdu-medium teachers",
      description:
        "A web app that helps teachers generate Urdu lesson plans with AI. Built around a clean, accessible interface with privacy-first handling of user data. The goal was to make the tool usable by educators who aren't technical.",
      features: [
        "AI-based Urdu lesson plan generation",
        "Clean and accessible UI",
        "Privacy-first user data handling",
        "Responsive design for all devices",
      ],
      technologies: ["PHP", "Laravel", "OpenAI", "Python", "Flask"],
      demoLink: "https://hikmat.ai",
      imgSrc: "project_hikmat.webp",
      featured: true,
    },
    {
      category: "Full-stack",
      title: "BServices",
      subtitle: "Digital agency website & inquiry system",
      description:
        "A professional agency site showcasing services, portfolio and client work, with an inquiry-management flow behind the contact forms and an SEO-optimized structure driving clear conversion paths.",
      features: [
        "Service showcase with detailed descriptions",
        "Portfolio section for completed projects",
        "Contact forms with inquiry management",
        "SEO-optimized structure",
      ],
      technologies: ["PHP", "jQuery", "MySQL", "CSS"],
      demoLink: "https://bservices.dev/",
      imgSrc: "project_bservices.webp",
      featured: false,
    },
    {
      category: ["AI", "Full-stack"],
      title: "Insta AI Tools",
      subtitle:
        "AI Instagram content studio with multi-tool generation & subscription billing",
      description:
        "A full-stack AI content platform for Instagram creators: captions, hashtags, bios, usernames, post ideas and images generated from a single studio in 20+ languages. React SPA on a Node/Express API with session auth, per-plan usage metering and Lemon Squeezy billing.",
      features: [
        "AI caption generator with tone and language control",
        "Hashtag research split into popular, niche, location-based & viral sets",
        "Profile bio optimizer driven by niche and keywords",
        "Username generator for on-brand Instagram handles",
        "Post-idea generator for viral hooks and content concepts",
        "AI image generation in square, portrait (4:5) and story/reel (9:16)",
        "Output in 20+ languages across every tool",
        "History and favorites with TXT / PNG export",
        "Email/password auth with session cookies and password reset",
        "Free, Pro & Business tiers with per-tool monthly usage limits",
        "Lemon Squeezy checkout and self-serve billing portal",
      ],
      technologies: [
        "React",
        "TypeScript",
        "Vite",
        "Tailwind CSS",
        "shadcn/ui",
        "TanStack Query",
        "Node.js",
        "Express",
        "Lemon Squeezy",
      ],
      demoLink: "https://instaaitool.com/",
      imgSrc: "project_instaaitool.webp",
      featured: true,
    },
    {
      category: "Full-stack",
      title: "Geofroggy",
      subtitle: "Human-centered global storytelling platform",
      description:
        "Reimagines geography through human stories. Instead of just marking coordinates on a map, it brings locations to life through memories, voices and lived experiences. A cultural archive where everyday traditions are shared and made personal.",
      features: [
        "Story-driven interactive world map",
        "User-generated content (voices, memories, traditions)",
        "Cultural archive of lived experiences",
        "PayPal integration and third-party APIs",
      ],
      technologies: ["Next.js", "TypeScript", "PayPal", "Figma"],
      demoLink: "https://geofroggy.com/",
      imgSrc: "project_geofroggy.webp",
      featured: true,
    },
    {
      category: "Frontend",
      title: "Ads Wala",
      subtitle:
        "Digital marketing agency website built for search visibility & lead generation",
      description:
        "A digital marketing agency website built for growth, not noise. Strategy-led service pages, high-performance design and technical SEO work together to turn organic search traffic into qualified leads and higher rankings for brands chasing the top spot.",
      features: [
        "Strategy-led service pages targeting high-intent search keywords",
        "Custom WordPress theme with reusable page-builder blocks",
        "Technical SEO: semantic markup, meta tags, Open Graph, schema and XML sitemaps",
        "Core Web Vitals tuning: caching, image optimization and lazy loading",
        "Conversion rate optimization with clear CTAs and lead-capture funnels",
        "Case studies and results showcase built to prove measurable ROI",
        "Content marketing blog with internal linking for organic traffic growth",
        "Multi-step contact and quote forms with inquiry management",
        "Client-editable CMS so the team ships content without a developer",
        "Fully responsive, accessible, mobile-first interface",
      ],
      technologies: [
        "WordPress",
        "PHP",
        "MySQL",
        "Elementor",
        "Rank Math SEO",
        "JavaScript",
        "SCSS",
      ],
      demoLink: "https://adswala.pk/",
      imgSrc: "project_adswala.webp",
      featured: true,
    },
    {
      category: "Frontend",
      title: "Zentry",
      subtitle: "Motion-led marketing site",
      description:
        "A sleek animated site built to show what considered motion design does for a marketing page: smooth transitions, interactive UI, and performance that holds up on mobile.",
      features: [
        "Smooth animations and scroll transitions",
        "Responsive and mobile-friendly design",
        "SEO optimized for performance",
        "Customizable sections and components",
      ],
      technologies: ["React", "Tailwind CSS", "Framer Motion", "Vite"],
      demoLink: "https://zentry.netlify.app/",
      imgSrc: "project_zentry.webp",
      featured: false,
    },
    {
      category: "AI",
      title: "Chasoteric™",
      subtitle: "AI astrology & cosmic insight platform",
      description:
        "Blends ancient wisdom with modern clarity: astrology, numerology and Chinese Zodiac insights delivered through CHAS, an AI celestial guide. Designed to make symbolic systems clear, trustworthy and accessible.",
      features: [
        "Astrology, numerology and Chinese Zodiac tools",
        "Powered by CHAS – Creative Honed Assistance System",
        "Culturally inclusive symbolic systems",
        "Design focused on clarity and trust",
      ],
      technologies: ["React", "Tailwind CSS", "Framer Motion", "OpenAI API"],
      demoLink: "https://chasoteric.com/",
      imgSrc: "project_chasoteric.webp",
      featured: false,
    },
    {
      category: "Frontend",
      title: "Mega News",
      subtitle: "React news portal with caching layer",
      description:
        "A responsive news portal where users browse, search and read articles. Built on Chakra UI for accessibility out of the box, with Axios-driven fetching and caching to keep the feed fast.",
      features: [
        "Responsive, accessible design using Chakra UI",
        "News fetching and caching with Axios",
        "Smooth animations with Framer Motion",
        "Routing handled by React Router DOM",
      ],
      technologies: ["React", "Chakra UI", "Framer Motion", "Sass"],
      demoLink: "",
      imgSrc: "project_news.webp",
      featured: false,
    },
  ],
};

/* Cream break section — process + the short version of who I am. */
export const approach = {
  eyebrow: "How I work",
  heading: "From problem to production",
  desc: "Three years of shipping taught me the same lesson repeatedly: the code is the easy part. Understanding the problem properly is what makes software worth maintaining.",
  steps: [
    {
      num: "01",
      title: "Scope",
      desc: "Understand the actual problem and who has it, before a line of code gets written. Cut what isn't needed.",
    },
    {
      num: "02",
      title: "Architect",
      desc: "Pick boring, proven tools. Design the data model and API surface so the thing can grow without a rewrite.",
    },
    {
      num: "03",
      title: "Build",
      desc: "Ship in vertical slices so frontend, backend and integrations land together, and there's something real to react to early.",
    },
    {
      num: "04",
      title: "Ship & own",
      desc: "Deploy, monitor, fix. I stay with what I build rather than handing it over and walking away.",
    },
  ],
  aboutHeading: "The short version",
  about: [
    "I'm Moosa, a full-stack engineer with 3+ years building web products, currently at Spire Pharmacy in the UK where I work on AI-assisted pharmacy software.",
    "I'm equally comfortable in a Laravel backend and a Next.js frontend, and I've spent the last stretch integrating AI into systems that real businesses depend on. I care about software that's reliable and maintainable more than software that's clever.",
  ],
};

export const stack = {
  eyebrow: "Stack",
  heading: "What I reach for",
  /* Four groups render automatically — Stack.jsx maps over this array.
     Payments/integrations split out of "tooling" so Stripe and PayPal
     aren't buried under an AI heading. */
  groups: [
    {
      label: "Frontend",
      items: [
        "JavaScript",
        "TypeScript",
        "React",
        "Next.js",
        "Tailwind CSS",
        "Sass",
        "Chakra UI",
        "Material UI",
        "Framer Motion",
      ],
    },
    {
      label: "Backend & APIs",
      items: [
        "PHP",
        "Laravel",
        "Sanctum",
        "Node.js",
        "Express",
        "REST APIs",
        "MySQL",
        "MongoDB",
      ],
    },
    {
      label: "AI",
      items: ["OpenAI API", "MCP", "Python", "Flask"],
    },
    {
      label: "Integrations & tooling",
      items: [
        "Stripe",
        "PayPal",
        "Git",
        "Vite",
        "Postman",
        "Figma",
        "Firebase",
      ],
    },
  ],
};

/* Experience and education merged into a single timeline. */
export const path = {
  eyebrow: "Path",
  heading: "Where I've been",
  timeline: [
    {
      kind: "work",
      current: true,
      title: "Software Engineer",
      org: "Spire Pharmacy",
      meta: "UK · Remote",
      date: "Jan 2025 – Present",
      points: [
        "Building scalable full-stack applications with React, Laravel and Node.js.",
        "Integrating AI into pharmacy software workflows.",
        "Working across teams to ship and optimize product delivery.",
      ],
      tech: ["React", "Laravel", "Node.js", "AI/ML", "MySQL", "MongoDB"],
    },
    {
      kind: "work",
      current: false,
      title: "Full Stack Developer",
      org: "MTAI Software Labs",
      meta: "Remote",
      date: "Apr 2023 – Dec 2024",
      points: [
        "Developed SaaS platforms in React and Node.js with scalability as the constraint.",
        "Implemented REST APIs and optimized backend performance.",
        "Led delivery on key SaaS features end to end.",
      ],
      tech: ["React", "Node.js", "MongoDB", "Express", "REST APIs"],
    },
    {
      kind: "work",
      current: false,
      title: "Backend Developer",
      org: "B Services",
      meta: "Remote",
      date: "Jul 2021 – Mar 2023",
      points: [
        "Built and maintained backend APIs and services.",
        "Handled system reliability and data security.",
        "Worked closely with frontend developers on integration.",
      ],
      tech: ["PHP", "Laravel", "MySQL", "Node.js", "Express"],
    },
    {
      kind: "study",
      current: false,
      cgpa: 3.5,
      title: "BS Computer Science",
      org: "Government College University, Lahore",
      meta: "Coursework in software engineering, web development and algorithms",
      date: null,
      points: [],
      tech: [],
    },
  ],
};

export const contact = {
  eyebrow: "Contact",
  heading: "Got something worth building?",
  desc: "Got a project, a role, or just a problem worth talking through? I read everything and reply within 24 hours.",
  email: "moosaishfaq461@gmail.com",
  github: "m-ishfaq",
  linkedin: "moosa-ishfaq",
  response: "Within 24 hours",
  available: [
    "Full-time opportunities",
    "Freelance projects",
    "Consulting & mentoring",
    "Open source contributions",
  ],
};

export const footer = {
  desc: "Full-stack engineer building reliable, AI-assisted web products.",
};
